import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

function parseDate(str: string | null): Date | null {
  if (!str) return null;
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const d = new Date(+match[1], +match[2] - 1, +match[3]);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function isToday(d: Date): boolean {
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

export function useDateParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Memo on the raw strings (not the searchParams object) so unrelated param
  // changes — e.g. ?view=tracked on /calendar — don't churn the Date instances
  // and trigger downstream effects that depend on them.
  const dateStr = searchParams.get("date");
  const fromStr = searchParams.get("from");
  const toStr = searchParams.get("to");

  const date = useMemo(() => parseDate(dateStr) ?? new Date(), [dateStr]);

  const dateRange = useMemo((): DateRange => {
    const from = parseDate(fromStr);
    const to = parseDate(toStr);
    return { from: from ?? undefined, to: to ?? undefined };
  }, [fromStr, toStr]);

  const setDate = useCallback(
    (d: Date) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("from");
        next.delete("to");
        if (isToday(d)) {
          next.delete("date");
        } else {
          next.set("date", formatDate(d));
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  const setDateRange = useCallback(
    (range: DateRange) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (range.from) next.set("from", formatDate(range.from));
        else next.delete("from");
        if (range.to) next.set("to", formatDate(range.to));
        else next.delete("to");
        if (range.from || range.to) next.delete("date");
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  return { date, dateRange, setDate, setDateRange };
}
